import React, {useState} from 'react';
import {
  HIDE_DEFAULT_INPUT_STYLES,
  CHECKED_CLASS,
  FOCUSED_CLASS,
  FOCUSED_VISIBLE_CLASS,
} from './constants';
import {px, boxShadow} from '@packages/systems/core/utils/WebflowCssSubset';
import {
  setAllBordersWidth,
  setAllBordersColor,
  setAllBordersStyle,
} from './utils';

import type {AtomRenderProps} from '@packages/systems/wfdl';
import {
  getInPojo,
  getArrayOrElse,
  getBooleanOrElse,
  getString,
} from '@packages/utilities/data-utils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';

const identity = (a: boolean | ((prevIsChecked?: any) => any)) => a;
const mockUseStateForSsr = (initialValue: any) => [initialValue, identity];

type FormCustomBooleanInputProps = AtomRenderProps & {
  isCheckbox: boolean;
};

export const FormCustomBooleanInput = ({
  attributes,
  classes,
  generateAttributes,
  data,
  isCheckbox,
}: FormCustomBooleanInputProps) => {
  const xattr = getArrayOrElse(getInPojo(data, ['xattr']), []);
  const propsIsChecked =
    getBooleanOrElse(
      getInPojo(data, ['temp', 'checkbox', 'isChecked']),
      false
    ) ||
    getBooleanOrElse(getInPojo(data, ['attr', 'checked']), false) ||
    xattr.some(
      (attr) =>
        getString(getInPojo(attr, ['name'])) === 'checked' &&
        getString(getInPojo(attr, ['value'])) === 'checked'
    );
  const propsIsFocused = getBooleanOrElse(
    getInPojo(data, ['temp', 'checkbox', 'isFocused']),
    false
  );
  const propsIsFocusedVisible = getBooleanOrElse(
    getInPojo(data, ['temp', 'checkbox', 'isFocusedVisible']),
    false
  );

  const runtimeEnvironment = useRuntimeEnvironment();
  const useStateHook =
    // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
    runtimeEnvironment === WebflowEnvironments.SSR
      ? mockUseStateForSsr
      : // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
        useState;

  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  const [isChecked, setIsChecked] = useStateHook(propsIsChecked);
  const isPreviewMode = usePreviewMode();

  const props = generateAttributes(attributes, classes);

  const {
    className,
    'data-w-id': dataWId,
    'data-wf-id': dataWfId,
    ...rest
  } = props;

  const inputProps = {
    ...rest,
    onChange: () => {
      setIsChecked((prevIsChecked: boolean) => !prevIsChecked);
    },
    // for checkbox (and not for radio) we need to keep input controlled in the Preview as otherwise change is not working correctly
    ...(isPreviewMode && isCheckbox ? {checked: isChecked} : {}),
  };

  const pseudoModeClasses = `${propsIsChecked ? ` ${CHECKED_CLASS}` : ''}${
    propsIsFocused ? ` ${FOCUSED_CLASS}` : ''
  }${
    propsIsFocusedVisible ? ` ${FOCUSED_CLASS} ${FOCUSED_VISIBLE_CLASS}` : ''
  }`;

  const currentClassName = isPreviewMode
    ? String(className)
    : `${className}${pseudoModeClasses}`;

  return (
    <>
      <div
        className={currentClassName}
        data-w-id={dataWId}
        data-wf-id={dataWfId}
      />
      <input {...inputProps} style={HIDE_DEFAULT_INPUT_STYLES} />
    </>
  );
};

export const customControlBaseStyle = {
  ...setAllBordersWidth(1),
  ...setAllBordersColor({r: 204, g: 204, b: 204, a: 1}),
  ...setAllBordersStyle('solid'),
  width: px(12),
  height: px(12),
} as const;

export const customControlFocusStyle = {
  boxShadow: boxShadow({
    xOffset: px(0),
    yOffset: px(0),
    blurRadius: px(3),
    spreadRadius: px(1),
    color: {
      r: 56,
      g: 152,
      b: 236,
      a: 1,
    },
  }),
} as const;
